import React from 'react';
import {cookieToObject} from '../helpers/auth';
import {useLazyGetProfileQuery, useLazyGetProfilesAndRolesQuery} from '../store/api/accountApi';
import {useLogoutMutation} from "../store/api/authApi";
import {messageType, showMessages} from '../helpers/message';
import {useAppSelector} from './useStore';
import {cookiesAuthenticated, selectIsMemberAuthenticated, selectIsStaff} from '../store/slices/userSlice';
import {dismissLoading} from '../components/OverlayLoadingIndicator';
import {BaseError} from '../types/apiTypes';

let cookies: any;

const useIsAuthenticated = (reAuthenticate = false) => {
    const userSliceAuth = useAppSelector(selectIsMemberAuthenticated);
    const isStaff = useAppSelector(selectIsStaff);

    const [getProfilesAndRoles] = useLazyGetProfilesAndRolesQuery();
    const [getProfile, {isLoading}] = useLazyGetProfileQuery();
    const [logout] = useLogoutMutation();

    const [loading, setLoading] = React.useState(reAuthenticate);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        // OverlayLoadingIndicator will only be visible when reAuthentication is true.
        if (reAuthenticate && !isLoading && !loading) {
            dismissLoading();
        }
    }, [reAuthenticate, isLoading, loading]);

    React.useEffect(() => {
        // Once getProfilesAndRoles authenticates the user, it will also set a staff profile if the account is staff.
        // Once the staff state updates, it will check the cookies to load back up the member profile if they were on one.
        if (isStaff) {
            cookies = cookies ?? cookieToObject();
            if (cookies["id"]) {
                getProfile(Number(cookies["id"]));
            }
        }
    }, [isStaff, getProfile]);

    React.useEffect(() => {
        // No need to run setLoginData if loading/reAuthenticate is false.
        if (!loading || !reAuthenticate) return;

        const setLoginData = async () => {
            try {
                await Promise.all([
                    getProfilesAndRoles().unwrap(), // Needs unwrap to handle errors.
                ]);
            } catch (e) {
                // With error when query roles (ie email hasn't been verified), run logout process.
                await logout();

                // The structure of incoming error may not always adhere to the BaseError.data.message structure,
                //  and may need to be updated to handle varying structures in the future.
                // Ie: newError.error.data.message, rather than newError.data.message
                const newError = e as BaseError;
                setError(newError?.data?.message ?? '');
                showMessages(newError?.data?.message, messageType.error);
            } finally {
                setLoading(false);
            }
        }

        if (!userSliceAuth) {
            const cookieAuth = cookiesAuthenticated();
            // If cookies are authenticated (which happens in processLogin), continue setting login data.
            if (cookieAuth) {
                setLoginData();
            } else {
                setLoading(false);
            }
        } else {
            setLoading(false);
        }
    }, [userSliceAuth, getProfilesAndRoles, loading, reAuthenticate, logout]);

    return {
        isAuthenticated: userSliceAuth,
        isLoading: loading || isLoading,
        error,
    }
}

export { useIsAuthenticated };
